import assert from 'assert';
import {AxiosError} from 'axios';
import {GetStaticPaths} from 'next';

import {
    AggregatorRuleTag,
    CertificationAggregatorApi,
    fetchCertificationAggregators,
    fetchOpenBadgedCertificationTopics,
} from '@udemy/browse-aggregator';
import {disallowSlug, RENDER_MODES} from '@udemy/nextjs-core';
import {CourseSearchTriggerType, fetchCourseSearch} from '@udemy/react-discovery-units';
import {udSentry} from '@udemy/sentry';

import {getLayout} from 'src/components/layout';
import {
    CertificationPreparationPageFeature,
    CertificationPreparationPageFeatureProps,
} from 'src/features/certification-preparation';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {fetcher} from 'src/lib/gql-fetcher';
import {AppContextData} from 'src/types/page-props';

import {CERTIFICATION_PREP_SEARCH_QUERY} from '../../../components/aggregator-course-directory/providers/aggregator-course-directory-context-provider.react-component';
import {CARD_CAROUSEL_PAGE_SIZE} from '../../../features/certification-aggregator/certification-aggregator';
import {udemyApi} from '../../../lib/ud-global-fetch';
import {getStaticAppContext} from '../../api/context.api';

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps = disallowSlug(async (context) => {
    const locale = context.locale as string;
    const api = new CertificationAggregatorApi(udemyApi, fetcher);

    const courseSearchContext = {
        triggerType: CourseSearchTriggerType.BrowseCertification,
    };
    const courseSearchVariables = {
        queryString: CERTIFICATION_PREP_SEARCH_QUERY,
        context: courseSearchContext,
    };

    let translations,
        staticData,
        issuerAggregatorsComponentInitialData,
        subjectAggregatorsComponentInitialData,
        certificationTopicCarouselComponentInitialData,
        courseSearchInitialData;

    try {
        [
            translations,
            staticData,
            issuerAggregatorsComponentInitialData,
            subjectAggregatorsComponentInitialData,
            certificationTopicCarouselComponentInitialData,
            courseSearchInitialData,
        ] = await Promise.all([
            getCompactTranslations(locale),
            getStaticAppContext({locale}),
            fetchCertificationAggregators(
                api,
                [AggregatorRuleTag.ISSUER],
                1,
                CARD_CAROUSEL_PAGE_SIZE,
                locale,
            ),
            fetchCertificationAggregators(
                api,
                [AggregatorRuleTag.SUBJECT],
                1,
                CARD_CAROUSEL_PAGE_SIZE,
                locale,
            ),
            fetchOpenBadgedCertificationTopics(
                api,
                'by_descriptor_type',
                'all',
                1,
                CARD_CAROUSEL_PAGE_SIZE,
            ),
            fetchCourseSearch(fetcher, {
                ...courseSearchVariables,
            }),
        ]);
    } catch (error) {
        if ((error as AxiosError).isAxiosError) {
            const axiosError = error as AxiosError;
            // eslint-disable-next-line no-console
            console.error(
                'Failed to fetch aggregators component data for Certification Prep Page due to axios error',
                {error_details: axiosError.toJSON(), locale},
            );
        } else {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch aggregators component data of Certification Prep Page', {
                error_details: {
                    message: (error as Error).message,
                    stack: (error as Error).stack,
                    cause: (error as Error).cause,
                },
                locale,
                courseSearchVariables,
            });
        }
        udSentry.captureException(error);
        throw error;
    }

    // For now, let's do not return empty arrays and not persist that in the cache.
    assert(
        issuerAggregatorsComponentInitialData?.content?.length > 0,
        'issuerAggregatorsComponentInitialData should not be empty',
    );

    assert(
        subjectAggregatorsComponentInitialData?.content?.length > 0,
        'subjectAggregatorsComponentInitialData should not be empty',
    );

    assert(
        certificationTopicCarouselComponentInitialData?.content?.length > 0,
        'certificationTopicCarouselComponentInitialData should not be empty',
    );

    assert(
        courseSearchInitialData?.courseSearch?.results?.length ?? 0 > 0,
        'courseSearchInitialData should not be empty',
    );

    const appContext: AppContextData = {...staticData};

    return {
        props: {
            appContext,
            locale,
            pageKey: 'certification_preparation_page',
            renderMode: RENDER_MODES.ISR,
            translations,
            issuerAggregatorsComponentInitialData,
            subjectAggregatorsComponentInitialData,
            certificationTopicCarouselComponentInitialData,
            courseSearchInitialData,
        },
        revalidate: FOUR_HOURS,
    };
});

function CertificationPreparationPage(props: CertificationPreparationPageFeatureProps) {
    return <CertificationPreparationPageFeature {...props} />;
}

CertificationPreparationPage.getLayout = getLayout;

export default CertificationPreparationPage;
